import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Grid, Input, Label, Select, Switch } from "theme-ui";
import moment from "moment";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { getRoles } from "api/Roles";
import AnnouncementModal from "components/Announcement/AnnouncementModal";
import { readingTime } from "components/Announcement/markdown";
import {
  Announcement,
  AnnouncementAudiences,
  AnnouncementPerson,
  archiveAnnouncement,
  createAnnouncement,
  duplicateAnnouncement,
  getAnnouncementEngagement,
  getAnnouncementSettings,
  listAnnouncements,
  publishAnnouncement,
  setAnnouncementSettings,
  updateAnnouncement,
} from "api/Announcements";

/**
 * Company Settings → Popups → ANNOUNCEMENTS (spec:
 * docs/announcements/ANNOUNCEMENTS_SPEC.md; mockups "Announcements mockups").
 *
 * Who uses it: Jack / staff, a few times a quarter, usually pasting a draft
 * written with an AI. They write it, pick the audience and dates, preview
 * it as each user type, publish, then check who saw / read / clicked.
 * Volume is a handful of announcements and a few hundred viewers each, so
 * plain lists, no paging.
 */

const card = { border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", marginTop: "20px" };
const labelSx = { fontFamily: "Poppins", fontSize: "12px", fontWeight: 600, color: "base_800", marginBottom: "4px", display: "block" };
const inputSx = { fontFamily: "Poppins", fontSize: "13px", borderColor: "#D6D6D3", borderRadius: "6px" };
const cell: React.CSSProperties = { fontFamily: "Poppins", fontSize: 12, padding: "10px 8px", borderBottom: "1px solid #F1EFE8", textAlign: "left", verticalAlign: "top" };
const chip = (bg: string, fg: string): React.CSSProperties => ({ background: bg, color: fg, borderRadius: 4, padding: "1px 6px", fontSize: 11, fontFamily: "Poppins", whiteSpace: "nowrap" });

const PATHS: Array<{ value: string; label: string }> = [
  { value: "/dashboard", label: "Dashboard" },
  { value: "/invoices", label: "Invoices" },
  { value: "/quotes", label: "Quotes" },
  { value: "/quote-requests", label: "RFQ's" },
  { value: "/projects", label: "Projects" },
  { value: "/purchase-orders", label: "Purchase orders (providers)" },
  { value: "/leads", label: "Leads (staff)" },
  { value: "/leads/opportunities", label: "Opportunities (staff)" },
];

type Draft = {
  id?: string;
  status?: Announcement["status"];
  title: string;
  body: string;
  actionLabel: string;
  actionPath: string;
  moreUrl: string;
  audiences: AnnouncementAudiences;
  startsAt: string;
  endsAt: string;
};

const emptyDraft = (): Draft => ({
  title: "",
  body: "",
  actionLabel: "",
  actionPath: "",
  moreUrl: "",
  audiences: { client: false, clientAdmin: false, provider: false, staff: false, staffRoleIds: [] },
  startsAt: moment().format("YYYY-MM-DD"),
  endsAt: moment().add(30, "days").format("YYYY-MM-DD"),
});

const toDraft = (a: Announcement): Draft => ({
  id: a.id,
  status: a.status,
  title: a.title,
  body: a.body ?? "",
  actionLabel: a.actionLabel ?? "",
  actionPath: a.actionPath ?? "",
  moreUrl: a.moreUrl ?? "",
  audiences: { staffRoleIds: [], ...(a.audiences ?? {}) },
  startsAt: a.startsAt ? moment(a.startsAt).format("YYYY-MM-DD") : "",
  endsAt: a.endsAt ? moment(a.endsAt).format("YYYY-MM-DD") : "",
});

const audienceChips = (a: AnnouncementAudiences, roles: Array<{ id: string; title: string }>) => {
  const out: string[] = [];
  if (a.client) out.push("Clients");
  if (a.clientAdmin) out.push("Client admins");
  if (a.provider) out.push("Providers");
  if (a.staff) {
    const ids = a.staffRoleIds ?? [];
    out.push(ids.length ? `Staff: ${ids.map((id) => roles.find((r) => r.id === id)?.title ?? "role").join(", ")}` : "All staff");
  }
  return out;
};

const windowText = (a: Announcement) => {
  const s = a.startsAt ? moment(a.startsAt).format("MMM D") : "On publish";
  const e = a.endsAt ? moment(a.endsAt).format("MMM D") : "30 days";
  return `${s} – ${e}`;
};

const statusChip = (a: Announcement) => {
  const now = moment();
  if (a.status === "draft") return <span style={chip("#F1EFE8", "#5F5E5A")}>Draft</span>;
  if (a.status === "archived" || (a.endsAt && moment(a.endsAt).isBefore(now))) return <span style={chip("#F1EFE8", "#5F5E5A")}>Ended</span>;
  if (a.startsAt && moment(a.startsAt).isAfter(now)) return <span style={chip("#EEF4FB", "#185FA5")}>Scheduled</span>;
  return <span style={chip("#E6F2EA", "#1E5C38")}>Live</span>;
};

const AnnouncementsCard: React.FC = () => {
  const toast = useToast();
  const [enabled, setEnabled] = useState(true);
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<Array<{ id: string; title: string }>>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [previewAs, setPreviewAs] = useState<"client" | "clientAdmin" | "provider" | "staff">("client");
  const [device, setDevice] = useState<"desktop" | "phone">("desktop");
  const [people, setPeople] = useState<{ a: Announcement; rows: AnnouncementPerson[] } | null>(null);
  const [peopleFilter, setPeopleFilter] = useState<"all" | "read" | "clicked" | "later" | "unread">("all");
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, settings] = await Promise.all([listAnnouncements(), getAnnouncementSettings()]);
    setLoading(false);
    if (Array.isArray(list)) setItems(list);
    else toast.add(list.error, "var(--theme-ui-colors-danger)");
    if (settings && !("error" in settings)) setEnabled(settings.enabled);
  }, []); // eslint-disable-line

  useEffect(() => {
    load();
    getRoles({ query: { recordsPer: 1000 } }).then((res: any) => {
      const list = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      setRoles(list.map((r: any) => ({ id: r.id, title: r.title })));
    });
  }, [load]);

  const toggleEnabled = async (on: boolean) => {
    setEnabled(on);
    const r: any = await setAnnouncementSettings(on);
    if (r?.error) {
      setEnabled(!on);
      return toast.add(r.error, "var(--theme-ui-colors-danger)");
    }
    toast.add(on ? "Announcements are on." : "Announcements are off - nobody sees one until you turn this back on.", "var(--theme-ui-colors-success)");
  };

  /* ------------------------------------------------------------ editor */

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setAud = (patch: Partial<AnnouncementAudiences>) => setDraft((d) => (d ? { ...d, audiences: { ...d.audiences, ...patch } } : d));

  /** Wraps the selection in the content box (or inserts at the cursor). */
  const format = (kind: "h" | "b" | "i" | "link" | "ul" | "ol") => {
    const el = bodyRef.current;
    if (!el || !draft) return;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const sel = value.slice(s, e);
    let next = value;
    let caret = e;
    const linePrefix = (prefix: (i: number) => string) => {
      const start = value.lastIndexOf("\n", s - 1) + 1;
      const block = value.slice(start, e) || "";
      const lines = (block || "").split("\n").map((l, i) => prefix(i) + l.replace(/^(#{1,3}\s+|[-*]\s+|\d+[.)]\s+)/, ""));
      next = value.slice(0, start) + lines.join("\n") + value.slice(e);
      caret = start + lines.join("\n").length;
    };
    if (kind === "b") { next = value.slice(0, s) + `**${sel || "bold text"}**` + value.slice(e); caret = s + (sel || "bold text").length + 4; }
    if (kind === "i") { next = value.slice(0, s) + `*${sel || "italic text"}*` + value.slice(e); caret = s + (sel || "italic text").length + 2; }
    if (kind === "link") { next = value.slice(0, s) + `[${sel || "link text"}](https://)` + value.slice(e); caret = s + (sel || "link text").length + 3; }
    if (kind === "h") linePrefix(() => "## ");
    if (kind === "ul") linePrefix(() => "- ");
    if (kind === "ol") linePrefix((i) => `${i + 1}. `);
    set({ body: next });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const payload = (d: Draft) => ({
    title: d.title,
    body: d.body,
    actionLabel: d.actionLabel || null,
    actionPath: d.actionLabel ? d.actionPath || null : null,
    moreUrl: d.moreUrl || null,
    audiences: d.audiences,
    startsAt: d.startsAt ? moment(d.startsAt, "YYYY-MM-DD").startOf("day").toISOString() : null,
    endsAt: d.endsAt ? moment(d.endsAt, "YYYY-MM-DD").endOf("day").toISOString() : null,
  });

  const save = async (andPublish: boolean) => {
    if (!draft) return;
    setBusy(andPublish ? "publish" : "save");
    const r: any = draft.id ? await updateAnnouncement(draft.id, payload(draft) as any) : await createAnnouncement(payload(draft) as any);
    if (r?.error) {
      setBusy(null);
      return toast.add(r.error, "var(--theme-ui-colors-danger)");
    }
    let final = r;
    if (andPublish && r.status !== "published") {
      final = await publishAnnouncement(r.id);
      if (final?.error) {
        setBusy(null);
        setDraft(toDraft(r));
        return toast.add(final.error, "var(--theme-ui-colors-danger)");
      }
    }
    setBusy(null);
    setDraft(null);
    load();
    toast.add(
      andPublish && r.status !== "published"
        ? `Published. ${final.startsAt && moment(final.startsAt).isAfter(moment()) ? `It starts ${moment(final.startsAt).format("MMM D")}` : "People see it at their next login"} and it turns off ${moment(final.endsAt).format("MMM D")}.`
        : "Saved.",
      "var(--theme-ui-colors-success)",
    );
  };

  const archive = async (a: Announcement) => {
    setBusy(`ar-${a.id}`);
    const r: any = await archiveAnnouncement(a.id);
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    toast.add("Ended. Nobody else will see it; who saw it is kept.", "var(--theme-ui-colors-success)");
    if (draft?.id === a.id) setDraft(null);
    load();
  };

  const duplicate = async (a: Announcement) => {
    setBusy(`du-${a.id}`);
    const r: any = await duplicateAnnouncement(a.id);
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    setDraft(toDraft(r));
    load();
  };

  const openPeople = async (a: Announcement) => {
    setBusy(`pe-${a.id}`);
    const r: any = await getAnnouncementEngagement(a.id);
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    setPeopleFilter("all");
    setPeople({ a, rows: r.people ?? [] });
  };

  const rt = draft ? readingTime(draft.body) : { words: 0, minutes: 0 };
  const audienceOn = draft ? !!draft.audiences[previewAs] : false;
  const roleLabel = { client: "clients", clientAdmin: "client admins", provider: "providers", staff: "staff" }[previewAs];
  const pathKnown = draft ? PATHS.some((p) => p.value === draft.actionPath) : true;

  const filteredPeople = (people?.rows ?? []).filter((p) =>
    peopleFilter === "all" ? true
      : peopleFilter === "read" ? !!p.reachedEndAt
      : peopleFilter === "clicked" ? !!p.clickedAt
      : peopleFilter === "later" ? !p.acknowledgedAt && !p.clickedAt && p.laterCount > 0
      : !p.reachedEndAt,
  );
  const outcome = (p: AnnouncementPerson) =>
    p.clickedAt ? "Clicked the button" : p.acknowledgedAt ? "Got it" : p.laterCount > 0 ? `Later ×${p.laterCount}` : "Open";

  return (
    <Box sx={card}>
      <Flex sx={{ justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, gap: "12px", flexWrap: "wrap" }}>
        <Box>
          <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>Announcements</Box>
          <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey }}>
            Shown once per person at login, one pop-up per login, to the audiences you pick.
          </Box>
        </Box>
        <Flex sx={{ alignItems: "center", gap: "14px" }}>
          <Flex as="label" sx={{ alignItems: "center", gap: "8px", fontFamily: "Poppins", fontSize: "12px" }}>
            <Box sx={{ width: "40px" }}>
              <Switch checked={enabled} onChange={(e: any) => toggleEnabled(e.target.checked)} aria-label="Show announcements" />
            </Box>
            {enabled ? "On" : "Off"}
          </Flex>
          <BasicButton type="button" keepText onClick={() => { setPeople(null); setDraft(emptyDraft()); }} disabled={!!draft}>
            New announcement
          </BasicButton>
        </Flex>
      </Flex>

      {/* List */}
      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead>
            <tr>
              <th style={{ ...cell, paddingLeft: 20, color: themevals.colors.dkgrey }}>Title</th>
              <th style={{ ...cell, color: themevals.colors.dkgrey }}>Audience</th>
              <th style={{ ...cell, color: themevals.colors.dkgrey }}>Window</th>
              <th style={{ ...cell, color: themevals.colors.dkgrey }}>Status</th>
              <th style={{ ...cell, color: themevals.colors.dkgrey }}>Seen</th>
              <th style={{ ...cell, color: themevals.colors.dkgrey }}>Read to end</th>
              <th style={{ ...cell, color: themevals.colors.dkgrey }}>Clicked</th>
              <th style={cell} />
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...cell, paddingLeft: 20, color: themevals.colors.dkgrey }}>
                  No announcements yet. Use New announcement to write the first one.
                </td>
              </tr>
            )}
            {items.map((a) => (
              <tr key={a.id}>
                <td style={{ ...cell, paddingLeft: 20, fontWeight: 500, maxWidth: 260 }}>{a.title}</td>
                <td style={cell}>
                  <Flex sx={{ gap: "4px", flexWrap: "wrap" }}>
                    {audienceChips(a.audiences ?? {}, roles).map((c) => (
                      <span key={c} style={chip("#EEF4FB", "#185FA5")}>{c}</span>
                    ))}
                  </Flex>
                </td>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>{windowText(a)}</td>
                <td style={cell}>{statusChip(a)}</td>
                <td style={cell}>{a.status === "draft" ? "—" : a.stats?.shown ?? 0}</td>
                <td style={cell}>{a.status === "draft" ? "—" : a.stats?.reachedEnd ?? 0}</td>
                <td style={cell}>{a.status === "draft" ? "—" : a.stats?.clicked ?? 0}</td>
                <td style={{ ...cell, whiteSpace: "nowrap", textAlign: "right", paddingRight: 20 }}>
                  <Flex sx={{ gap: "6px", justifyContent: "flex-end" }}>
                    {a.status !== "archived" && (
                      <BasicButton outline scheme="secondary" type="button" keepText onClick={() => { setPeople(null); setDraft(toDraft(a)); }}>Edit</BasicButton>
                    )}
                    {a.status !== "draft" && (
                      <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === `pe-${a.id}`} onClick={() => openPeople(a)}>Who saw it</BasicButton>
                    )}
                    {a.status === "published" ? (
                      <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === `ar-${a.id}`} onClick={() => archive(a)}>End now</BasicButton>
                    ) : a.status === "archived" ? (
                      <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === `du-${a.id}`} onClick={() => duplicate(a)}>Duplicate</BasicButton>
                    ) : null}
                  </Flex>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* Who saw it */}
      {people && (
        <Box sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "16px 20px" }}>
          <Flex sx={{ justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "10px" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>{`Who saw “${people.a.title}”`}</Box>
            <Flex sx={{ gap: "8px", alignItems: "center" }}>
              <Select value={peopleFilter} onChange={(e) => setPeopleFilter(e.target.value as any)} sx={{ ...inputSx, padding: "6px 30px 6px 10px" }} aria-label="Filter people">
                <option value="all">Everyone shown it ({people.rows.length})</option>
                <option value="read">Read to the end</option>
                <option value="unread">Did not reach the end</option>
                <option value="clicked">Clicked the button</option>
                <option value="later">Remind me later / closed</option>
              </Select>
              <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setPeople(null)}>Close</BasicButton>
            </Flex>
          </Flex>
          <Box sx={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead>
                <tr>
                  {["Person", "Organization", "Audience", "First shown", "Times", "Read to end", "Outcome"].map((h) => (
                    <th key={h} style={{ ...cell, color: themevals.colors.dkgrey, position: "sticky", top: 0, background: "#FFFFFF" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPeople.length === 0 && (
                  <tr><td colSpan={7} style={{ ...cell, color: themevals.colors.dkgrey }}>Nobody here yet.</td></tr>
                )}
                {filteredPeople.map((p) => (
                  <tr key={p.userId}>
                    <td style={cell}><Box sx={{ fontWeight: 500 }}>{p.name}</Box><Box sx={{ color: themevals.colors.dkgrey }}>{p.email}</Box></td>
                    <td style={cell}>{p.organization ?? "—"}</td>
                    <td style={cell}>{{ client: "Client", clientAdmin: "Client admin", provider: "Provider", staff: "Staff" }[p.audience ?? ""] ?? "—"}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>{p.firstShownAt ? moment(p.firstShownAt).format("MMM D, h:mm a") : "—"}</td>
                    <td style={cell}>{p.timesShown}</td>
                    <td style={cell}>{p.reachedEndAt ? "Yes" : "No"}</td>
                    <td style={cell}>{outcome(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Editor + live preview */}
      {draft && (
        <Grid columns={[1, null, "minmax(0, 1fr) minmax(0, 1fr)"]} gap={0} sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}` }}>
          <Box sx={{ padding: "18px 20px", borderRight: [0, null, `1px solid ${themevals.colors.inputOutline}`], display: "flex", flexDirection: "column", gap: "14px" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>
              {draft.id ? (draft.status === "published" ? "Edit live announcement" : "Edit announcement") : "New announcement"}
            </Box>

            <Box>
              <Label htmlFor="ann-title" sx={labelSx}>Title</Label>
              <Input id="ann-title" maxLength={120} value={draft.title} onChange={(e) => set({ title: e.target.value })} sx={inputSx} />
            </Box>

            <Box>
              <Label htmlFor="ann-body" sx={labelSx}>Content</Label>
              <Box sx={{ border: "1px solid #D6D6D3", borderRadius: "6px", overflow: "hidden" }}>
                <Flex sx={{ gap: "2px", padding: "4px 6px", borderBottom: "1px solid #ECECEA", background: "#FAFAF8", alignItems: "center", flexWrap: "wrap" }}>
                  {([
                    ["h", "Heading", <span key="h" style={{ fontWeight: 600 }}>H</span>],
                    ["b", "Bold", <span key="b" style={{ fontWeight: 700 }}>B</span>],
                    ["i", "Italic", <span key="i" style={{ fontStyle: "italic", fontFamily: "Georgia, serif" }}>I</span>],
                    ["link", "Link", <span key="l" style={{ textDecoration: "underline" }}>Link</span>],
                    ["ul", "Bulleted list", <span key="u">• List</span>],
                    ["ol", "Numbered list", <span key="o">1. List</span>],
                  ] as Array<[any, string, React.ReactNode]>).map(([k, label, icon]) => (
                    <button key={k} type="button" aria-label={label} title={label} onClick={() => format(k)} style={{ minWidth: 34, height: 32, border: 0, background: "transparent", cursor: "pointer", fontFamily: "Poppins, sans-serif", fontSize: 12, borderRadius: 4 }}>
                      {icon}
                    </button>
                  ))}
                  <Box sx={{ marginLeft: "auto", fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey, paddingRight: "6px" }}>Paste Markdown from your AI draft</Box>
                </Flex>
                <textarea
                  id="ann-body"
                  ref={bodyRef}
                  rows={12}
                  value={draft.body}
                  onChange={(e) => set({ body: e.target.value })}
                  style={{ width: "100%", boxSizing: "border-box", border: 0, padding: "10px 12px", fontFamily: "Menlo, Consolas, monospace", fontSize: 12, lineHeight: 1.6, resize: "vertical", outline: "none" }}
                />
              </Box>
              <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "4px" }}>
                {`About ${rt.minutes} min read · ${rt.words} words`}{rt.words > 150 ? " · long: the pop-up scrolls, and headings help it read as groups" : ""}
              </Box>
            </Box>

            <Grid columns={[1, 2]} gap="12px">
              <Box>
                <Label htmlFor="ann-btn" sx={labelSx}>Button label (optional)</Label>
                <Input id="ann-btn" maxLength={60} placeholder="e.g. View my invoices" value={draft.actionLabel} onChange={(e) => set({ actionLabel: e.target.value })} sx={inputSx} />
              </Box>
              <Box>
                <Label htmlFor="ann-path" sx={labelSx}>Button goes to</Label>
                <Select id="ann-path" disabled={!draft.actionLabel} value={pathKnown ? draft.actionPath : "__other"} onChange={(e) => set({ actionPath: e.target.value === "__other" ? "/" : e.target.value })} sx={inputSx}>
                  <option value="">Pick a page…</option>
                  {PATHS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  <option value="__other">Other page…</option>
                </Select>
                {!pathKnown && draft.actionLabel && (
                  <Input aria-label="Page path" placeholder="/path/in/the/app" value={draft.actionPath} onChange={(e) => set({ actionPath: e.target.value })} sx={{ ...inputSx, marginTop: "6px" }} />
                )}
              </Box>
            </Grid>

            <Box>
              <Label htmlFor="ann-more" sx={labelSx}>Read more link (optional)</Label>
              <Input id="ann-more" placeholder="https://… or /page" value={draft.moreUrl} onChange={(e) => set({ moreUrl: e.target.value })} sx={inputSx} />
            </Box>

            <Box>
              <Box sx={labelSx}>Show to</Box>
              <Flex sx={{ gap: "16px", flexWrap: "wrap", fontFamily: "Poppins", fontSize: "13px" }}>
                {([["client", "Clients"], ["clientAdmin", "Client admins"], ["provider", "Providers"], ["staff", "Staff"]] as const).map(([k, label]) => (
                  <label key={k} style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!draft.audiences[k]} onChange={(e) => setAud({ [k]: e.target.checked } as any)} style={{ width: 16, height: 16, accentColor: "#111111" }} />
                    {label}
                  </label>
                ))}
              </Flex>
              {draft.audiences.staff && roles.length > 0 && (
                <Box sx={{ marginTop: "8px", padding: "8px 10px", background: "#FAFAF8", borderRadius: "6px", fontFamily: "Poppins", fontSize: "12px" }}>
                  <Box sx={{ color: themevals.colors.dkgrey, marginBottom: "6px" }}>Which staff? None ticked = all staff.</Box>
                  <Flex sx={{ gap: "12px", flexWrap: "wrap" }}>
                    {roles.map((r) => {
                      const ids = draft.audiences.staffRoleIds ?? [];
                      return (
                        <label key={r.id} style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={ids.includes(r.id)}
                            onChange={(e) => setAud({ staffRoleIds: e.target.checked ? [...ids, r.id] : ids.filter((x) => x !== r.id) })}
                            style={{ width: 15, height: 15, accentColor: "#111111" }}
                          />
                          {r.title}
                        </label>
                      );
                    })}
                  </Flex>
                </Box>
              )}
            </Box>

            <Grid columns={[1, 2]} gap="12px">
              <Box>
                <Label htmlFor="ann-start" sx={labelSx}>Starts</Label>
                <Input id="ann-start" type="date" value={draft.startsAt} onChange={(e) => set({ startsAt: e.target.value })} sx={inputSx} />
              </Box>
              <Box>
                <Label htmlFor="ann-end" sx={labelSx}>Turns off</Label>
                <Input id="ann-end" type="date" value={draft.endsAt} onChange={(e) => set({ endsAt: e.target.value })} sx={inputSx} />
              </Box>
            </Grid>

            <Flex sx={{ justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
              <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setDraft(null)} disabled={!!busy}>Cancel</BasicButton>
              {draft.status === "published" ? (
                <BasicButton type="button" keepText onClick={() => save(false)} disabled={!!busy}>{busy === "save" ? "Saving…" : "Save changes"}</BasicButton>
              ) : (
                <>
                  <BasicButton outline scheme="secondary" type="button" keepText onClick={() => save(false)} disabled={!!busy}>{busy === "save" ? "Saving…" : "Save draft"}</BasicButton>
                  <BasicButton type="button" keepText onClick={() => save(true)} disabled={!!busy}>{busy === "publish" ? "Publishing…" : "Publish"}</BasicButton>
                </>
              )}
            </Flex>
          </Box>

          <Box sx={{ padding: "18px 20px", background: "#F7F7F5", display: "flex", flexDirection: "column", gap: "12px", minWidth: 0 }}>
            <Flex sx={{ justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>Preview</Box>
              <Flex role="tablist" aria-label="Preview as" sx={{ background: "#EAEAE7", borderRadius: "6px", padding: "2px" }}>
                {([["client", "Client"], ["clientAdmin", "Client admin"], ["provider", "Provider"], ["staff", "Staff"]] as const).map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={previewAs === k} onClick={() => setPreviewAs(k)} style={{ border: 0, background: previewAs === k ? "#FFFFFF" : "transparent", borderRadius: 5, padding: "6px 10px", fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: previewAs === k ? 600 : 400, cursor: "pointer" }}>
                    {label}
                  </button>
                ))}
              </Flex>
              <Flex role="tablist" aria-label="Screen" sx={{ background: "#EAEAE7", borderRadius: "6px", padding: "2px" }}>
                {(["desktop", "phone"] as const).map((k) => (
                  <button key={k} type="button" role="tab" aria-selected={device === k} onClick={() => setDevice(k)} style={{ border: 0, background: device === k ? "#FFFFFF" : "transparent", borderRadius: 5, padding: "6px 10px", fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: device === k ? 600 : 400, cursor: "pointer", textTransform: "capitalize" }}>
                    {k}
                  </button>
                ))}
              </Flex>
            </Flex>
            <Box sx={{ position: "relative", borderRadius: "8px", background: "rgba(17,17,17,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: device === "phone" ? "16px" : "20px", height: device === "phone" ? 640 : 520 }}>
              <Box sx={{ width: device === "phone" ? 330 : "100%", maxWidth: 560, height: device === "phone" ? "100%" : "auto", maxHeight: "100%", display: "flex", opacity: audienceOn ? 1 : 0.35 }}>
                <AnnouncementModal preview={device} announcement={{ title: draft.title, body: draft.body, actionLabel: draft.actionLabel || null, actionPath: draft.actionPath || null, moreUrl: draft.moreUrl || null }} />
              </Box>
              {!audienceOn && (
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "#FFFFFF", borderRadius: "6px", padding: "10px 14px", fontFamily: "Poppins", fontSize: "13px", fontWeight: 500 }}>
                  {`Not shown to ${roleLabel} (not ticked).`}
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      )}
    </Box>
  );
};

export default AnnouncementsCard;
